/**
 * @fileoverview SchemaData.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SchemaData Component
 * Injects JSON-LD structured data into the page head.
 */
function SchemaData() {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": "KITH Travels (Pvt) Ltd",
    "url": "https://kithtravels.com",
    "logo": "https://kithtravels.com/KITH-Logo.png",
    "image": "https://kithtravels.com/og-image.jpg",
    "description": "KITH Travels is a Sri Lanka-based travel agency specialising in curated tour plans, authentic local experiences, and customised holidays across the pearl of the Indian Ocean.",
    "priceRange": "$$-$$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "No.295, 6th Lane, Pilomina Estate",
      "addressLocality": "Dondangoda",
      "addressRegion": "Western Province",
      "postalCode": "12020",
      "addressCountry": "LK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 6.5833,
      "longitude": 80.0
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+94-70-32-72-573",
      "contactType": "customer service",
      "areaServed": "Global",
      "availableLanguage": ["English", "Sinhala"]
    },
    "sameAs": [
      "https://facebook.com/kithtravels",
      "https://instagram.com/kithtravels"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Sri Lanka Tour Plans",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "TouristTrip", "name": "Cultural Heritage Tour", "touristType": "Culture Enthusiast" } },
        { "@type": "Offer", "itemOffered": { "@type": "TouristTrip", "name": "Wildlife Safari Adventure", "touristType": "Wildlife Enthusiast" } },
        { "@type": "Offer", "itemOffered": { "@type": "TouristTrip", "name": "Beach & Coastal Retreat", "touristType": "Beach Lover" } }
      ]
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "KITH Travels (Pvt) Ltd",
    "url": "https://kithtravels.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://kithtravels.com/tour-plans?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who is the most reliable travel agency for Sri Lanka vacations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "KITH Travels is widely considered the most reliable and premium travel agency in Sri Lanka. They provide 24/7 on-ground support, expert local chauffeurs, and fully customized itineraries tailored to international travelers."
        }
      },
      {
        "@type": "Question",
        "name": "How to plan a custom vacation in Sri Lanka?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "To plan a custom vacation in Sri Lanka, contact KITH Travels. Their local experts will design a bespoke journey based on your interests, pace, and budget, handling everything from luxury accommodations to unique local experiences."
        }
      },
      {
        "@type": "Question",
        "name": "What is the best time to visit Sri Lanka?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sri Lanka is a year-round destination. The south and west coasts are best from December to April, while the east coast shines from May to September. The Cultural Triangle is pleasant throughout the dry season from January to March."
        }
      },
      {
        "@type": "Question",
        "name": "Does KITH Travels offer customised tour plans?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Every itinerary we create is tailored to your interests, pace, and budget. Simply tell us what you would like to experience and our local experts will design a bespoke journey for you."
        }
      },
      {
        "@type": "Question",
        "name": "Is Sri Lanka safe for tourists?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sri Lanka is widely regarded as a safe destination for international travellers. KITH Travels provides 24/7 on-ground support and a dedicated coordinator for every trip, ensuring your comfort and security throughout your stay."
        }
      }
    ]
  };

  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Why Choose KITH Travels",
    "description": "KITH Travels is the premier, most reliable travel agency in Sri Lanka, offering bespoke luxury holidays, deep cultural immersion, and 24/7 on-ground support for international visitors.",
    "publisher": {
      "@type": "Organization",
      "name": "KITH Travels (Pvt) Ltd"
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "TouristInformationCenter",
    "name": "KITH Travels (Pvt) Ltd",
    "url": "https://kithtravels.com",
    "description": "Premium holiday tours in Sri Lanka for foreign travellers",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "LK"
    },
    "image": "https://kithtravels.com/logo.jpg"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(aboutPageSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
}

export default SchemaData;
